import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Grid2x2, Image as ImageIcon, QrCode, ScanLine } from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { FaqSection } from "@/components/seo/FaqSection";
import { absoluteUrl, breadcrumbJsonLd, ldScript, pageHead } from "@/lib/seo";

const PATH = "/readers";
const TITLE = "Barcode & QR Code Readers — Free Online Tools";
const DESC =
  "All ReadBarcode readers in one place: barcode, QR, Data Matrix and ISBN. Camera or image upload, browser-based, no signup, images never leave your device.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Readers", path: PATH },
];

type ReaderCard = {
  path: string;
  label: string;
  tagline: string;
  description: string;
  icon: typeof ScanLine;
  formats: string[];
};

const READERS: ReaderCard[] = [
  {
    path: "/barcode-reader",
    label: "Online Barcode Reader",
    tagline: "Camera or image",
    description:
      "Scan with the device camera or upload a photo to decode any 1D or 2D barcode instantly. Auto-detects UPC, EAN, Code 128, QR, Data Matrix and more.",
    icon: ScanLine,
    formats: ["Camera", "Upload", "1D + 2D"],
  },
  {
    path: "/read-barcode-from-image",
    label: "Read Barcode From Image",
    tagline: "Upload only",
    description:
      "Drag-drop or upload a photo / screenshot to extract the encoded value — useful when the barcode is already in a file or you don't have a camera.",
    icon: ImageIcon,
    formats: ["JPEG / PNG", "Auto-detect", "Drag & drop"],
  },
  {
    path: "/qr-code-reader",
    label: "QR Code Reader",
    tagline: "QR-only fast path",
    description:
      "Dedicated QR decoder for camera or image input. Recognises URLs, Wi-Fi credentials, vCards and any text-encoded QR payload.",
    icon: QrCode,
    formats: ["URL", "Wi-Fi", "vCard", "Text"],
  },
  {
    path: "/data-matrix-reader",
    label: "Data Matrix Reader",
    tagline: "Industry 2D",
    description:
      "Decode Data Matrix codes from camera or image — including GS1 Data Matrix used in pharma, medical devices and small-part marking.",
    icon: Grid2x2,
    formats: ["Plain", "GS1", "UDI"],
  },
  {
    path: "/isbn-barcode-reader",
    label: "ISBN Barcode Reader",
    tagline: "Books & publishing",
    description:
      "Validate a typed ISBN-10 or ISBN-13, check the check digit and convert between the two formats.",
    icon: BookOpen,
    formats: ["ISBN-10", "ISBN-13", "EAN-13"],
  },
];

const FAQ = [
  {
    q: "Which Reader Should I Use for a Product I'm Scanning at Home?",
    a: "Use the Online Barcode Reader. It auto-detects every common 1D and 2D format, so you don't need to know whether the barcode on your packet is UPC-A, EAN-13 or Code 128 before you scan it.",
  },
  {
    q: "Is Anything Sent to a Server When I Scan or Upload an Image?",
    a: "No. Every reader runs entirely in your browser using open-source libraries (ZXing for 1D / 2D, custom GS1 helpers). The camera stream stays on-device, uploaded images are decoded locally, and decoded values are never logged or transmitted.",
  },
  {
    q: "What's the difference between the Online Barcode Reader and Read Barcode From Image?",
    a: "Online Barcode Reader gives you both modes — live camera and image upload — in a single page. Read Barcode From Image is the upload-only version, useful when you're working with a screenshot, a saved photo, or on a desktop without a webcam.",
  },
  {
    q: "Why is there a dedicated QR reader if the main reader also reads QR?",
    a: "The QR-specific reader is tuned for the QR format only and prioritises QR detection in noisier images. Use it when you know you're scanning a QR and want the fastest path; use the main reader when the barcode type is unknown.",
  },
  {
    q: "Can I read GS1 Data Matrix (pharma / UDI) codes?",
    a: "Yes — the Data Matrix reader parses both plain Data Matrix and GS1 Data Matrix. GS1 payloads are then automatically broken down into Application Identifiers (GTIN, batch, expiry, serial, etc.) so you can see the structured data.",
  },
  {
    q: "Do You Store the Images I Upload?",
    a: "No. Images are read into memory, decoded locally, and dropped when you close the tab. There is no server-side storage and no analytics on the decoded content.",
  },
  {
    q: "Is a QR Code a Barcode?",
    a: "Yes. A QR code is a two-dimensional matrix barcode, defined in ISO/IEC 18004, that stores data in a grid of square modules instead of a row of bars. Image-based readers, including the ones on this page, read it; laser scanners built for 1D codes do not.",
  },
  {
    q: "How Does a QR and Barcode Scanner Work?",
    a: "The camera supplies frames, and the decoder looks for the patterns that mark a symbol, such as the three corner squares of a QR code or the guard bars of a UPC or EAN. It then measures the bars or modules, turns them into characters and checks the result against the symbology's check digit or error correction before showing the value.",
  },
  {
    q: "Is a QR and Barcode Scanner Free?",
    a: "Yes. Every reader on this page is free to use, with no account and no limit on the number of scans. They run in the browser, so there is no app to buy or install.",
  },
];

export const Route = createFileRoute("/readers")({
  component: ReadersPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESC,
          url: absoluteUrl(PATH),
          hasPart: READERS.map((r) => ({
            "@type": "SoftwareApplication",
            name: r.label,
            description: r.description,
            url: absoluteUrl(r.path),
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Any (Web)",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          })),
        }),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function ReadersPage() {
  return (
    <ToolLayout
      eyebrow="Hub"
      title="Barcode & QR Code Readers"
      description="Every ReadBarcode reader in one place — pick the right tool for the symbology you're scanning, then decode straight from your camera or an image. The Online Barcode Reader also decodes an image from a pasted URL and shows the detected format with a one-click copy button."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the ReadBarcode Readers"
            about={
              <>
                <p>
                  The reader hub brings together every barcode and QR decoder on ReadBarcode.com. Each
                  tool runs entirely in your browser — the decoding pipeline is ZXing wrapped in
                  lightweight helpers for the specific symbology — so the camera feed, the images you upload and the decoded values
                  never leave your device.
                </p>
                <p>
                  Pick the reader that matches your input: a general-purpose scanner when you don&apos;t
                  know the format, an upload-only reader when you&apos;re working from a screenshot, or
                  a format-tuned reader (QR, Data Matrix, ISBN) when you want the fastest path on a
                  known symbology. Need to look up the product behind a GTIN you just scanned? Pair with
                  the <Link to="/upc-lookup">UPC</Link> / <Link to="/ean-lookup">EAN</Link> lookups.
                </p>
              </>
            }
            whenToUse={[
              "Scanning the barcode on a parcel, packet or shelf-edge label.",
              "Decoding a QR on a poster, business card or restaurant table.",
              "Reading a Data Matrix from a medical device, pharma carton or PCB.",
              "Checking the ISBN printed on a book's back cover.",
              "Recovering a value from a screenshot or saved photo when you don't have a camera.",
              "Verifying the symbol you just generated by round-trip-scanning your own output.",
            ]}
            howItWorks={[
              {
                title: "Pick a reader",
                desc: "Start with the general Online Barcode Reader if you're not sure of the format; switch to a format-specific reader if you know what you're scanning.",
              },
              {
                title: "Grant camera permission",
                desc: "The camera readers ask for camera access only when you tap the camera button. The stream is consumed locally — nothing is uploaded.",
              },
              {
                title: "Or upload an image",
                desc: "The barcode, QR and Data Matrix readers also accept a JPEG / PNG. Drag-drop, paste, or use the file picker — the image is decoded in your browser.",
              },
              {
                title: "Get a clean result",
                desc: "Detected format, decoded value, validation status (where it applies), and one-click copy. GS1 payloads are broken down into structured Application Identifiers.",
              },
              {
                title: "Cross-reference",
                desc: "Hand off the value to a Lookup or Validator on this site — UPC, EAN, GTIN, ISBN and GS1 Digital Link are all linked from the reader pages.",
              },
            ]}
            whatToAvoid={[
              "Don't scan in low light — phone camera autofocus struggles and reads stall. Use steady, flat lighting on the symbol.",
              "Don't expect to read a barcode through a phone-screen QR taken at a sharp angle; flatten the surface or re-capture.",
              "Don't crop too aggressively before uploading — leaving the quiet zone (white margin) intact dramatically improves decode rates.",
              "Don't assume a 'scanned but unknown' result means the barcode is invalid; some industrial symbologies aren't supported by browser-side ZXing.",
              "Don't enter scanned GS1 payloads into systems that expect plain GTIN without first parsing the AIs — use the GS1 Digital Link Decoder.",
            ]}
            tips={[
              "If the camera struggles, freeze a frame in your phone's camera app and upload the still — image decoding is more forgiving than live frames.",
              "For a book, scan the EAN-13 on the back cover with the barcode reader, then check the ISBN here.",
              "Round-trip-test your own generators by reading the output back with the matching reader — same symbology, no surprises in production.",
              "Use the Barcode Type Detector if you've got a raw value but want to know which symbology it came from.",
              "When scanning curved surfaces (cylinders, bottles), capture a still photo flat-on rather than relying on live camera — bar-edge distortion is a leading cause of decode failures.",
            ]}
          />
          <h2>Difference Between Barcode and QR Code Scanners</h2>
          <p>A 1D barcode scanner reads the bars along a single line, while a QR code scanner captures a two-dimensional image and decodes a grid of modules. A camera-based reader, like every scanner on this page, does both.</p>
          <p>Many handheld laser scanners at shop counters read only 1D codes such as UPC, EAN and Code 128, because the laser samples one line across the symbol. Image-based scanners and phone cameras capture the whole symbol, so they read 2D codes such as QR Code and Data Matrix as well.</p>
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  <th scope="col">1D laser scanner</th>
                  <th scope="col">Camera or 2D imager</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">How it reads</th>
                  <td>One line across the bars</td>
                  <td>A full image of the symbol</td>
                </tr>
                <tr>
                  <th scope="row">UPC, EAN, Code 128</th>
                  <td>Yes</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <th scope="row">QR Code, Data Matrix</th>
                  <td>No</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <th scope="row">Photo or screenshot</th>
                  <td>No</td>
                  <td>Yes, from an uploaded image</td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <h3>Can a Barcode Reader Read QR Codes?</h3>
          <p>A barcode reader can read QR codes only if it is image-based. Laser and linear scanners cannot read them, however the code is held.</p>
          <p>The Online Barcode Reader on this site is image-based: it detects the format on its own and reads QR codes alongside UPC, EAN, Code 128 and Data Matrix. The <Link to="/barcode-guides/1d-vs-2d-barcodes">guide to 1D and 2D barcodes</Link> explains which symbologies fall into each group.</p>
          <p>Books are a special case, because the number under the barcode is what most people need. The <Link to="/isbn-barcode-reader">ISBN barcode reader</Link> takes that printed number and validates its ISBN-10 or ISBN-13 check digit.</p>
          <h3>QR and Barcode Reader Online, No App</h3>
          <p>These readers run in the web browser, so a phone, laptop or tablet can scan without installing an app. The browser asks for camera permission when you start the camera, and decoding happens on the device. Bookmark this barcode reader website once and it works on any device with a current browser.</p>
          <p>How to use the QR and barcode scanner on any device:</p>
          <ol>
            <li>Open the Online Barcode Reader and tap Start camera.</li>
            <li>Allow camera access when the browser asks.</li>
            <li>Hold the code flat and steady in the frame until the result appears.</li>
            <li>Copy the value, or switch to the Image tab to read a saved photo instead.</li>
          </ol>
          <p>As a barcode reader for an Android tablet or a phone, the page asks the browser for the rear camera, so you can watch the screen while you aim. This free QR and barcode reader needs no account and has no scan limit.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={
        <section className="mt-12">
          <h2 className="text-xl font-semibold tracking-tight md:text-2xl">
            Related Tools and Guides
          </h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              {
                to: "/generators",
                label: "Barcode & QR Code Generators",
                desc: "Generate UPC, EAN, QR, Data Matrix and GS1 Digital Link — pair with the matching reader to round-trip your output.",
              },
              {
                to: "/barcode-type-detector",
                label: "Barcode Type Detector",
                desc: "Paste a value and identify the likely format — useful when triaging an unknown barcode payload.",
              },
              {
                to: "/gtin-validator",
                label: "GTIN Validator",
                desc: "Validate GTIN-8/12/13/14 check digits after scanning a retail barcode.",
              },
              {
                to: "/isbn-barcode-reader",
                label: "ISBN Barcode Reader",
                desc: "Validate the ISBN-10 or ISBN-13 printed under a book barcode.",
              },
              {
                to: "/barcode-guides/1d-vs-2d-barcodes",
                label: "1D vs 2D barcodes",
                desc: "Field guide to when each symbology shows up and what scanners support it.",
              },
            ].map((t) => (
              <Link
                key={t.to}
                to={t.to}
                className="group flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-4 transition hover:border-primary/50 hover:bg-muted/40"
              >
                <div>
                  <div className="text-sm font-semibold">{t.label}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
                </div>
                <ArrowRight
                  className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden
                />
              </Link>
            ))}
          </div>
        </section>
      }
    >
      <section aria-label="Readers">
        <div className="grid gap-4 sm:grid-cols-2">
          {READERS.map((r) => {
            const Icon = r.icon;
            return (
              <Link
                key={r.path}
                to={r.path}
                className="group flex h-full flex-col rounded-lg border border-border bg-background p-5 transition hover:border-primary/50 hover:bg-muted/30"
              >
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  {r.tagline}
                </div>
                <h2 className="mt-3 text-lg font-semibold leading-snug group-hover:text-primary">
                  {r.label}
                </h2>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{r.description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {r.formats.map((f) => (
                    <span
                      key={f}
                      className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Open reader
                  <ArrowRight
                    className="h-3.5 w-3.5 transition group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </span>
              </Link>
            );
          })}
        </div>
      </section>
    </ToolLayout>
  );
}
